import React, { useEffect, useState } from 'react';
import { Grid, Card, CardActions, CardContent, CardHeader, useTheme } from '@material-ui/core';
import { useHistory } from 'react-router-dom';

// styles
import useStyles from './styles';

// components
import { Typography, Button } from '../../components/Wrappers';

// context
import { usePracticeDispatch, usePracticeState, actions as practiceActions } from '../../context/PracticeContext';
import { useOrderDispatch, useOrderState, actions as orderActions } from '../../context/OrderContext';
import {
    allStatesAndCountries,
    auStates,
    getOrdersForTerritories,
    getRefinementOrdersForTerritories,
    getThisWeekOrders,
} from '../order/helpers/report';
import Widget from '../../components/Widget/Widget';
import ReactApexChart from 'react-apexcharts';
import moment from 'moment';
import { chartOptions } from './chart';

/**
 * Get the start date of the current week
 * Format with ordinal suffix (e.g., "17th March")
 */
const getThisWeekStartDate = () => {
    const monday = moment().startOf('isoWeek');
    return monday.format('Do MMMM');
};

const Dashboard = () => {
    const theme = useTheme();
    const classes = useStyles();
    const history = useHistory();
    const practiceDispatch = usePracticeDispatch();
    const practiceState = usePracticeState();
    const orderDispatch = useOrderDispatch();
    const orderState = useOrderState();

    const [ordersReport, setOrdersReport] = useState(null);

    /**
     * Fetch orders on component mount to display the weekly report
     * Fetch Practices as soon as possible as it is needed in most workflows
     */
    useEffect(() => {
        if (!orderState.orders) {
            orderActions.doFetch()(orderDispatch);
        }
        if (!practiceState.practices) {
            practiceActions.doFetch()(practiceDispatch);
        }
    }, []); // eslint-disable-line

    /**
     * Calculate the weekly report when orders are fetched
     */
    useEffect(() => {
        if (!orderState.orders) {
            return;
        }

        if (orderState.orders.count > 0) {
            const thisWeekOrders = getThisWeekOrders(orderState.orders.items);
            const auOrders = getOrdersForTerritories(thisWeekOrders, auStates);
            const newCalOrders = getOrdersForTerritories(thisWeekOrders, ['NewCal-1']);
            const nzOrders = getOrdersForTerritories(thisWeekOrders, ['NZ-1']);
            const tahitiOrders = getOrdersForTerritories(thisWeekOrders, ['Tahiti-1']);
            const allRefinements = getRefinementOrdersForTerritories(thisWeekOrders, allStatesAndCountries);

            setOrdersReport([auOrders.length, newCalOrders.length, nzOrders.length, tahitiOrders.length, allRefinements.length]);
        } else {
            setOrdersReport([0, 0, 0, 0, 0]);
        }
    }, [orderState.orders]);

    const CreateNewOrderCard = () => (
        <Card className={classes.cardMedia}>
            <CardHeader title="Create Nova Order" />
            <CardContent>
                <Typography variant="body2" color="textSecondary" component="p">
                    Upload the 3D scan files of a patient and submit a new order to request a retainer treatment
                </Typography>
            </CardContent>
            <CardActions>
                <Button size="small" color="secondary" onClick={() => history.push('/app/nova-order/add')}>
                    Create
                </Button>
            </CardActions>
        </Card>
    );

    const CreateOrderCard = () => (
        <Card className={classes.cardMedia}>
            <CardHeader title="Create 3Shape Order" />
            <CardContent>
                <Typography variant="body2" color="textSecondary" component="p">
                    Upload the 3D scan files of a patient and submit a new order to request a retainer treatment
                </Typography>
            </CardContent>
            <CardActions>
                <Button size="small" color="secondary" onClick={() => history.push('/app/order/add')}>
                    Create
                </Button>
            </CardActions>
        </Card>
    );

    const ViewOrdersCard = () => (
        <Card className={classes.cardMedia}>
            <CardHeader title="View Orders" />
            <CardContent>
                <Typography variant="body2" color="textSecondary" component="p">
                    View a list of the recent orders, check their status and see the details for each one
                </Typography>
            </CardContent>
            <CardActions>
                <Button
                    size="small"
                    color="secondary"
                    onClick={() => history.push('/app/order/list?orderStatus=in-progress')}
                >
                    View
                </Button>
            </CardActions>
        </Card>
    );

    const CreatePracticeCard = () => (
        <Card className={classes.cardMedia}>
            <CardHeader title="Create Practice" />
            <CardContent>
                <Typography variant="body2" color="textSecondary" component="p">
                    Create an account for a new practice
                </Typography>
            </CardContent>
            <CardActions>
                <Button size="small" color="secondary" onClick={() => history.push('/app/practice/add')}>
                    Create
                </Button>
            </CardActions>
        </Card>
    );

    const ViewPracticesCard = () => (
        <Card className={classes.cardMedia}>
            <CardHeader title="View Practices" />
            <CardContent>
                <Typography variant="body2" color="textSecondary" component="p">
                    View a list of the practices account
                </Typography>
            </CardContent>
            <CardActions>
                <Button size="small" color="secondary" onClick={() => history.push('/app/practice/list')}>
                    View
                </Button>
            </CardActions>
        </Card>
    );

    const CreateRefinementCard = () => (
        <Card className={classes.cardMedia}>
            <CardHeader title="Create Refinement" />
            <CardContent>
                <Typography variant="body2" color="textSecondary" component="p">
                    View a list of the complete orders and select the one one you want to refine
                </Typography>
            </CardContent>
            <CardActions>
                <Button
                    size="small"
                    color="secondary"
                    onClick={() => history.push('/app/order/list?orderStatus=complete')}
                >
                    Create
                </Button>
            </CardActions>
        </Card>
    );

    return (
        <Grid container spacing={4}>
            <Grid item md={12} xs={12}>
                <Widget
                    title={`Report - Week of ${getThisWeekStartDate()}`}
                    disableWidgetMenu
                    className={classes.cardMedia}
                >
                    {!ordersReport || ordersReport.every((value) => value === 0) ? (
                        <Typography variant="body2" color="textSecondary" component="p" style={{ paddingTop: 10 }}>
                            No orders have been placed this week
                        </Typography>
                    ) : (
                        <ReactApexChart options={chartOptions(theme)} series={ordersReport} type="donut" height="275" />
                    )}
                </Widget>
            </Grid>
            <Grid item md={6} xs={12}>
                <CreateNewOrderCard />
            </Grid>
            <Grid item md={6} xs={12}>
                <CreateOrderCard />
            </Grid>
            <Grid item md={6} xs={12}>
                <ViewOrdersCard />
            </Grid>
            <Grid item md={6} xs={12}>
                <CreatePracticeCard />
            </Grid>
            <Grid item md={6} xs={12}>
                <ViewPracticesCard />
            </Grid>
            <Grid item md={6} xs={12}>
                <CreateRefinementCard />
            </Grid>
        </Grid>
    );
};

export default Dashboard;
